import { FilterOptionList, type FilterOption } from '@/components/filters/filter-menu'
import { FilterInlineDateCalendar } from '@/components/filters/filter-inline-date-calendar'

/** Shared date-range value shape for list filters. */
export type DateRange = {
  start_date?: string
  end_date?: string
}

/** `date` — YYYY-MM-DD (activities). `datetime` — full ISO strings (symptoms). */
export type DateRangeMode = 'date' | 'datetime'

type DateRangePreset = 'all' | 'today' | 'week' | 'month' | 'custom'

const PRESET_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'custom', label: 'Custom range' },
]

function serialize(date: Date, mode: DateRangeMode): string {
  return mode === 'date' ? date.toISOString().split('T')[0] : date.toISOString()
}

function computePreset(preset: DateRangePreset, mode: DateRangeMode): DateRange {
  const today = new Date()
  switch (preset) {
    case 'today':
      return { start_date: serialize(today, mode), end_date: serialize(today, mode) }
    case 'week': {
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay())
      startOfWeek.setHours(0, 0, 0, 0)
      return { start_date: serialize(startOfWeek, mode), end_date: serialize(today, mode) }
    }
    case 'month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      return { start_date: serialize(startOfMonth, mode), end_date: serialize(today, mode) }
    }
    case 'custom':
      return { start_date: serialize(today, mode), end_date: serialize(today, mode) }
    default:
      return {}
  }
}

function matchesPreset(value: DateRange, preset: 'today' | 'week' | 'month', mode: DateRangeMode): boolean {
  const expected = computePreset(preset, mode)
  const day = (iso?: string) => iso?.slice(0, 10)
  return day(value.start_date) === day(expected.start_date) && day(value.end_date) === day(expected.end_date)
}

function resolvePreset(value: DateRange, mode: DateRangeMode): DateRangePreset {
  if (!value.start_date && !value.end_date) return 'all'
  for (const preset of ['today', 'week', 'month'] as const) {
    if (matchesPreset(value, preset, mode)) return preset
  }
  return 'custom'
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Chip display: preset name when recognisable, otherwise “12 Jun – 10 Jul”. */
export function formatDateRangeDisplay(value: DateRange, mode: DateRangeMode): string {
  const preset = resolvePreset(value, mode)
  if (preset !== 'custom') {
    return PRESET_OPTIONS.find((o) => o.value === preset)?.label ?? 'All time'
  }
  const from = value.start_date ? formatDay(value.start_date) : '…'
  const to = value.end_date ? formatDay(value.end_date) : '…'
  return `${from} – ${to}`
}

export function isDateRangeActive(value: DateRange): boolean {
  return Boolean(value.start_date || value.end_date)
}

export type DateRangeControlProps = {
  value: DateRange
  onChange: (next: DateRange) => void
  mode: DateRangeMode
}

/** Popover body for the shared Date range dimension: presets + custom pickers. */
export function DateRangeControl({ value, onChange, mode }: DateRangeControlProps) {
  const preset = resolvePreset(value, mode)

  return (
    <div className="space-y-2">
      <FilterOptionList
        type="single"
        options={PRESET_OPTIONS}
        value={preset}
        onValueChange={(next) => onChange(computePreset(next as DateRangePreset, mode))}
      />
      {preset === 'custom' || (value.start_date && preset !== 'all') ? (
        <div className="grid grid-cols-1 border-t border-border/60 sm:grid-cols-2">
          <FilterInlineDateCalendar
            label="Start date"
            date={value.start_date ? new Date(value.start_date) : undefined}
            onSelect={(d) => onChange({ ...value, start_date: d ? serialize(d, mode) : undefined })}
            showClearButton
            initialFocus
          />
          <FilterInlineDateCalendar
            label="End date"
            date={value.end_date ? new Date(value.end_date) : undefined}
            onSelect={(d) => onChange({ ...value, end_date: d ? serialize(d, mode) : undefined })}
            showClearButton
          />
        </div>
      ) : null}
    </div>
  )
}
